function initWangsUIStore() {
    if (!window.WangsUI) {
        window.WangsUI = {
            presets: {
                'WANGS-TAB-MENU': {},
                'WANGS-INPUT-OTP': {},
            },
        };
    }
}
/**
 * Sets a preset configuration for a specific WangsUI web component.
 *
 * This function ensures type safety by enforcing that the value passed
 * matches the expected type for the given tag name, as defined in `WangsUIPresets`.
 *
 * @template K - The key of the component tag (e.g., 'WANGS-TAB-MENU') in `WangsUIPresets`.
 * @param {K} tagName - The UPPERCASE custom element tag name (e.g., 'WANGS-TAB-MENU').
 * @param {WangsUIPresets[K]} value - The configuration object for the given tag, must match the expected preset type.
 *
 * @example
 * setPreset('WANGS-TAB-MENU', {
 *   items: ['Home', 'Settings'],
 *   selectedIndex: 0,
 * });
 */
function setPreset(tagName, value) {
    initWangsUIStore();
    window.WangsUI.presets[tagName] = value;
}
/**
 * Retrieves a preset configuration for a specific WangsUI web component.
 *
 * @template K - The key of the component tag in `WangsUIPresets`.
 * @param tagName - The web component tag name (e.g., 'WANGS-TAB-MENU').
 * @returns The configuration object for the specified tag, or `undefined` if not set.
 *
 * @example
 * const preset = getPreset('WANGS-TAB-MENU');
 * console.log(preset?.items); // ['Home', 'Settings']
 */
function getPreset(tagName) {
    initWangsUIStore();
    return window.WangsUI.presets[tagName];
}
function usePreset(tag, override) {
    return { ...getPreset(tag), ...override };
}
/**
 * Tailwind class function helper to preserve literal types and enable IntelliSense.
 *
 * Ensures the preset structure matches the tag's expected type.
 *
 * @param tag - Component tag name (must be a key in WangsUIPresets)
 * @param preset - The preset object matching the type for the tag
 * @returns The same preset with literal types preserved.
 */
function definePreset(tag, preset) {
    return { [tag]: preset }[tag];
}

var tabmenu = definePreset('WANGS-TAB-MENU', {
    root: {
        class: 'overflow-x-visible',
    },
    menu: ({ props }) => ({
        class: [
            // Flexbox
            'flex flex-1',
            // Spacing
            'list-none',
            'p-0 m-0',
            // Colors
            'text-general-100',
            { 'w-max ring-1 ring-inset ring-general-100 rounded': props.type === 'segmented' },
        ],
    }),
    menuitem: {
        class: 'mr-0',
    },
    action: ({ props, context }) => ({
        class: [
            'relative',
            // Font
            'font-medium',
            'text-xs leading-4 tracking-[0.02em]',
            // Flexbox and Alignment
            'flex items-center',
            // Spacing
            {
                'rounded-xl py-1 px-3': props.type === 'pill',
                'rounded': props.type === 'segmented',
                'p-2 pb-1.5 pt-0.5 !h-6': props.type === 'tab',
                'py-[5px] px-3 !h-6': props.type === 'segmented',
                'ml-1': props.type === 'pill' && context.index !== 0,
                'gap-1': context.item.icon,
            },
            // Shape
            {
                'border-b-2': props.type === 'tab',
                'shadow-[-2px_2px_2px_rgba(0,0,0,0.25)] overflow-visible': props.type === 'segmented' && context.active,
            },
            // Colors and Conditions
            {
                'border-primary-700': context.active,
                'border-grayscale-400': !context.active,
                'text-grayscale-600': !context.active && props.type === 'tab',
                'text-primary-700': context.active && props.type === 'tab',
                'bg-primary-700 text-white': props.type === 'pill' && context.active,
                'bg-grayscale-100 text-grayscale-800': props.type === 'pill' && !context.active,
                'bg-primary-500 text-white': props.type === 'segmented' && context.active,
                '!text-general-800': props.type === 'segmented' && !context.active,
                'bg-transparent ': props.type !== 'pill' && !context.active,
            },
            // States
            { 'hover:bg-primary-50': props.type === 'tab' },
            'focus-visible:outline-none focus-visible:outline-offset-0',
            {
                'focus-visible:border-primary-200 focus-visible:text-primary-400': !context.active,
            },
            // Disabled States
            {
                'pointer-events-none': context.item?.disabled,
                'opacity-60': context.item?.disabled,
            },
            // Transitions
            'transition-all duration-200',
            // Misc
            'cursor-pointer select-none text-decoration-none',
            'overflow-hidden',
            'user-select-none',
            '!no-underline',
            'whitespace-nowrap',
        ],
    }),
    icon: {
        class: 'mr-2',
    },
    inkbar: ({ props }) => ({
        class: ['grow border-b-2 border-grayscale-400', { hidden: props.type === 'pill' || props.useTrailingLine === false || props.type === 'segmented' }],
    }),
});

var inputotp = definePreset('WANGS-INPUT-OTP', {
    root: {
        class: [
            // Alignment
            'flex items-center',
            'gap-[6px]',
            'w-max'
        ],
    },
    input: ({ props, context, parent }) => ({
        class: [
            // Font
            'text-[14px] leading-none font-medium',
            // Flex & Alignment
            { 'flex-1 w-[1%]': parent.tagName == 'WANGS-INPUT-GROUP' },
            'text-center',
            // Spacing
            'm-0',
            // Size
            'w-[42px] h-[42px]',
            // Shape
            { 'rounded-md': parent.tagName !== 'WANGS-INPUT-GROUP' },
            {
                'first:rounded-l-md rounded-none last:rounded-r-md': parent.tagName == 'WANGS-INPUT-GROUP',
            },
            {
                'border-0 border-y border-l last:border-r': parent.tagName == 'WANGS-INPUT-GROUP',
            },
            // Colors
            'text-general-800 dark:text-general-800',
            'placeholder:text-surface-400 dark:placeholder:text-surface-500',
            'bg-white dark:bg-white',
            {
                'ring-1': navigator.userAgent.includes('Firefox'),
                'ring-[0.5px]': !navigator.userAgent.includes('Firefox'),
            },
            { 'ring-general-200 dark:ring-general-200': !props.invalid },
            // Invalid State
            { 'ring-danger-500 dark:ring-danger-500': props.invalid },
            // States
            {
                'hover:bg-primary-50/50 hover:ring-primary-500 dark:hover:ring-primary-400': !context.disabled && !props.invalid,
                'focus:bg-primary-50/50 focus:ring-primary-500 dark:focus:ring-primary-400': !context.disabled && !props.invalid,
                'focus:outline-none focus:outline-offset-0': !context.disabled,
                'opacity-60 select-none pointer-events-none cursor-default': context.disabled,
            },
            // Filled State *for FloatLabel
            { filled: parent.tagName == 'WANGS-FLOAT-LABEL' && context.filled },
            // Misc
            'appearance-none',
            'transition-colors duration-200',
        ],
    }),
});

/**
 * A singleton utility class for handling SPA route navigation using the browser's History API.
 * Supports base URL registration per app instance and emits custom navigation events.
 */
class WangsUIRouter {
    static SINGLETON_KEY = '__WANGS_UI_ROUTER_INSTANCE__';
    constructor() {
        // Return existing instance if already created
        if (globalThis[WangsUIRouter.SINGLETON_KEY]) {
            return globalThis[WangsUIRouter.SINGLETON_KEY];
        }
        // Save instance to globalThis
        globalThis[WangsUIRouter.SINGLETON_KEY] = this;
    }
    /**
     * Stores base URLs associated with different app identifiers.
     */
    _baseUrl = '';
    /**
     * Registers a base URL for a specific app.
     *
     * @param base - The base URL to be used as a prefix for all routes in that app.
     */
    setNavigationBaseUrl(base) {
        this._baseUrl = this.normalizeBase(base);
    }
    /**
     * Retrieves the base URL for a specific app.
     *
     * @returns The base URL registered for the app or an empty string if not set.
     */
    getNavigationBaseUrl() {
        return this._baseUrl;
    }
    /**
     * Ensures the base URL ends with a single slash and removes trailing slashes.
     *
     * @param base - The base URL to normalize.
     * @returns A clean base URL ending with a single `/`.
     */
    normalizeBase(base) {
        return base.replace(/\/+$/, '').concat('/');
    }
    /**
     * Prepends the base URL (if any) to the given path.
     *
     * @param path - The relative path to normalize.
     * @returns A fully normalized path prefixed with the base URL if applicable.
     */
    normalizePath(path) {
        if (!path.startsWith('/')) {
            // The path must be relative to current location path
            // E.g., path = 'module', location '/base/currentpath'
            // return '/base/module'
            return new URL(path, window.location.href).pathname;
        }
        const base = this.getNavigationBaseUrl();
        if (!base)
            return path;
        const sanitizedPath = path.slice(1);
        return `${base}${sanitizedPath}`;
    }
    /**
     * Removes the specified base path from the beginning of a given route path.
     *
     * This is useful when your app is deployed under a subdirectory (e.g., `/base`)
     * and you want to normalize the route path to match menu or route definitions
     * that don't include the base.
     *
     * @param path - The full route path (e.g., `/base/about`).
     * @param base - The optional base URL to remove (e.g., `/base`).
     * @returns The normalized path without the base prefix (e.g., `/about`).
     *
     * @example
     * stripBase('/base/home', '/base'); // returns '/home'
     * stripBase('/about', '/base');     // returns '/about' (base not present)
     * stripBase('/base', '/base');      // returns '/'
     */
    stripBase(path, base) {
        base ||= this.getNavigationBaseUrl();
        return base !== '/' && path.startsWith(base) ? '/' + path.slice(base.length).replace(/^\/+/, '') : path;
    }
    /**
     * Pushes a new entry to the browser's history stack.
     *
     * @param route - The target URL or path to push. Can be a string, URL object, or null.
     * @param data - Optional state object associated with the history entry. Accessible via `history.state`.
     * @param unused - Optional title parameter (currently unused due to limited browser support).
     *
     * @example
     * Router.push('/about', { from: 'home' });
     */
    push(route, data = {}, unused = '') {
        const state = {
            ...data,
            ...(history.state ?? {}),
            current: route,
            back: history.state?.current,
            forward: undefined,
            replaced: false,
            position: history.length + 1,
        };
        window.history.pushState(state, unused, route);
    }
    /**
     * Replaces the current entry in the browser's history stack.
     *
     * @param route - The target URL or path to replace with. Can be a string, URL object, or null.
     * @param data - Optional state object associated with the history entry. Accessible via `history.state`.
     * @param unused - Optional title parameter (currently unused due to limited browser support).
     *
     * @example
     * Router.replace('/login', { reason: 'session expired' });
     */
    replace(route, data = {}, unused = '') {
        const state = {
            ...data,
            ...(history.state ?? {}),
            forward: undefined,
            current: route,
            replaced: true,
        };
        window.history.replaceState(state, unused, route);
    }
    /**
     * Dispatches a custom event before navigation occurs.
     * Used to notify subscribers of an upcoming route change.
     *
     * @param route - The route that will be navigated to.
     * @param mode - The navigation mode ('push' or 'replace').
     * @param data - Optional state object for the navigation.
     */
    dispatchBeforeNavigationEvent(route, mode, data) {
        window.dispatchEvent(new CustomEvent('wangs-ui-core:before-navigation-event', {
            detail: { route, mode, state: data },
        }));
    }
    /**
     * Dispatches a custom event after navigation occurs.
     * Used to notify subscribers that the route has changed.
     *
     * @param route - The route that was navigated to.
     * @param mode - The navigation mode ('push' or 'replace').
     * @param data - Optional state object for the navigation.
     */
    dispatchNavigationEvent(route, mode, data) {
        window.dispatchEvent(new CustomEvent('wangs-ui-core:navigation-event', {
            detail: { route, mode, state: data },
        }));
    }
    /**
     * Navigates to a specified route using the browser's History API.
     *
     * This updates the URL in the browser without reloading the page.
     * It can either push a new entry to the history stack or replace the current one.
     * Also dispatches custom events before and after the navigation.
     *
     * @param route - The target route to navigate to. Can be a string or URL object.
     * @param navigationMode - Determines how navigation is handled.
     *                          - `'push'` (default): Adds a new entry to the browser history.
     *                          - `'replace'`: Replaces the current history entry.
     * @param data - Optional state object associated with the history entry. Can be accessed via `history.state`.
     * @param unused - Optional title parameter (currently unused due to browser support limitations).
     *
     * @example
     * router.navigateToRoute('/about', 'push', { some: 'state' }, '',);
     */
    navigateToRoute(route, navigationMode = 'push', data = history.state, unused = '') {
        if (!route)
            return;
        const rawUrl = typeof route === 'string' ? route : route.toString();
        const finalUrl = this.normalizePath(rawUrl);
        if (finalUrl === location.pathname) {
            return console.warn('[WangsUIRouter] Navigation aborted: target route is the same as the current route.');
        }
        this.dispatchBeforeNavigationEvent(finalUrl, navigationMode, data);
        if (navigationMode === 'push') {
            this.push(finalUrl, data, unused);
        }
        else {
            this.replace(finalUrl, data, unused);
        }
        this.dispatchNavigationEvent(finalUrl, navigationMode, data);
    }
}
const router = new WangsUIRouter();

export { initWangsUIStore as a, definePreset as d, getPreset as g, inputotp as i, router as r, setPreset as s, tabmenu as t, usePreset as u };
